import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import domains, { DOMAIN_COLORS } from '../../data/domains';
import preferences from '../../data/preferences';
import usePayment from '../../hooks/usePayment';
import { USER_TIERS, getTierFromStorage, setTierToStorage } from '../../constants/tiers';
import UpgradePrompt from '../freemium/UpgradePrompt';
import CosmicIcon from '../common/CosmicIcon';

import ResultsProcessor from '../../utils/ResultsProcessor';
import { auth } from '../../lib/firebase';
import { getResultsForUser, getResultsByEmail, getResultById } from '../../lib/assessmentData';
import { useAuth } from '../../contexts/AuthContext';
import { launchIdentity } from '../../lib/identity';

// Cosmic Components
import CosmicResultsNavigation from './cosmic/CosmicResultsNavigation';
import CosmicDomainStrengths from './cosmic/CosmicDomainStrengths';
import CosmicShareWithCoach from './cosmic/CosmicShareWithCoach';
import CosmicShareButton from './cosmic/CosmicShareButton';
import CosmicConstellationTab from './cosmic/CosmicConstellationTab';
import CosmicDomainsGalaxy from './cosmic/CosmicDomainsGalaxy';
import CosmicCareerDirections from './cosmic/CosmicCareerDirections';
import { CosmicStarGridStandalone } from './cosmic/CosmicConstellationTab';

// Import cosmic design tokens
import '../../styles/cosmic-design-tokens.css';
import './cosmic/CosmicResultsView.css';

// Archetype name lookup based on top 2 domains
// Updated to match canonical names from constellations.js
const ARCHETYPE_NAMES = {
  'insight-vision': 'Luminary',
  'vision-insight': 'Luminary',
  'vision-impact': 'Trailblazer',
  'impact-vision': 'Trailblazer',
  'connection-impact': 'Spark',
  'impact-connection': 'Spark',
  'insight-structure': 'Scholar',
  'structure-insight': 'Scholar',
  'connection-structure': 'Harborer',
  'structure-connection': 'Harborer',
  'insight-connection': 'Storykeeper',
  'connection-insight': 'Storykeeper',
  'vision-structure': 'Shaper',
  'structure-vision': 'Shaper',
  'vision-connection': 'Dreamweaver',
  'connection-vision': 'Dreamweaver',
  'impact-structure': 'Anchor',
  'structure-impact': 'Anchor',
  'insight-impact': 'Navigator',
  'impact-insight': 'Navigator'
};

// Get constellation name from results or compute from domains
const getConstellationName = (results) => {
  // First try direct constellation data
  if (results?.constellationData?.name) {
    return results.constellationData.name;
  }
  
  // Compute from top 2 domains
  if (results?.domains) {
    const topDomains = Object.entries(results.domains)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 2)
      .map(([id]) => id);
    
    const key = `${topDomains[0]}-${topDomains[1]}`;
    return ARCHETYPE_NAMES[key] || 'Your Constellation';
  }
  
  return 'Your Constellation';
};

/**
 * Cosmic Results View - Enhanced version with cosmic styling
 * Preserves ALL original functionality while applying cosmic design system
 */
const CosmicResultsView = ({ results: propResults, tier: tierProp, shared = false }) => {
  // Get user tier: prefer prop (test page), then a result loaded by id
  // (coach viewing a client's result), then the authoritative tier for THIS
  // result.
  //
  // We can't trust localStorage `userTier` alone: the cosmic engine writes it
  // UPPERCASE while the legacy AssessmentProvider that wraps /results rejects
  // anything non-lowercase and clobbers it back to 'free'. So an Enhanced/Premium
  // result would render as Free. Instead resolve tier from the URL `?tier=` param
  // and the tier saved with the result (set by navigateToResults), normalized to
  // lowercase, only falling back to stored userTier.
  const resolveResultTier = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlTier = params.get('tier');
      if (urlTier) return urlTier.toLowerCase();
      const stored = localStorage.getItem('cosmicAssessmentResults');
      if (stored) {
        const savedTier = JSON.parse(stored)?.tier;
        if (savedTier) return String(savedTier).toLowerCase();
      }
    } catch (_) { /* fall through to storage */ }
    return (getTierFromStorage() || 'free').toLowerCase();
  };
  const storedTier = resolveResultTier();
  const [overrideTier, setOverrideTier] = useState(null);
  const testingTier = tierProp || overrideTier || storedTier;

  const { openPayment, modal: paymentModal } = usePayment();
  const navigate = useNavigate();

  // Coach/staff viewing a client's result via /results?resultId=... The assessment
  // app runs on a DIFFERENT origin than the CRM, so the viewer's Firebase Auth
  // session does not carry over and the Firestore read is denied until they sign
  // in here. Track auth + load errors so we can show a sign-in gate instead of an
  // endless "Loading your cosmic constellation..." spinner.
  const { isAuthenticated, loading: authLoading, signInWithGoogle, signOut } = useAuth();
  const [coachViewError, setCoachViewError] = useState('');
  const [coachSigningIn, setCoachSigningIn] = useState(false);

  // Post-payment continuation: when an upgrade payment succeeds (threaded up from
  // CosmicCheckoutModal via usePayment -> UpgradePrompt), persist the newly granted
  // tier and show a clear interstitial. On Continue we re-enter the cosmic flow at
  // the new tier so the engine administers the next stage's items (Enhanced = Stage-2
  // confirmation of the top 8; Premium = instant unlock from stored Enhanced data),
  // then renders the upgraded result. `?tier=` is the load-bearing signal the flow
  // reads on mount, so this survives the legacy provider clobbering userTier.
  const [pendingUpgradeTier, setPendingUpgradeTier] = useState(null);

  const handleUpgradeSuccess = (newTier) => {
    const upper = String(newTier || '').toUpperCase();
    if (!['ENHANCED', 'PREMIUM'].includes(upper)) return;
    setTierToStorage(upper);
    setPendingUpgradeTier(upper.toLowerCase());
  };

  const continueUpgrade = () => {
    const tierLower = pendingUpgradeTier;

    // The cosmic engine re-reads `cosmic-registration-data` on mount and (a) syncs
    // userTier FROM its `selectedTier` (overriding the URL ?tier=), and (b) bounces
    // Enhanced/Premium to registration unless `emailVerified` or `userId` is set. The
    // original Free taker registered with selectedTier:'FREE' and may have paid as a
    // guest-who-signed-in (no userId in the blob). Patch it so the continuation lands
    // on Stage-2 instead of restarting Free or bouncing to registration. The taker is
    // necessarily signed in here (payment requires auth).
    try {
      const raw = localStorage.getItem('cosmic-registration-data');
      const reg = raw ? JSON.parse(raw) : {};
      reg.selectedTier = tierLower.toUpperCase();
      if (auth.currentUser) {
        reg.userId = reg.userId || auth.currentUser.uid;
        reg.email = reg.email || auth.currentUser.email || '';
        reg.emailVerified = true;
        if (!reg.name) {
          reg.name = auth.currentUser.displayName || (reg.email || '').split('@')[0] || 'Explorer';
        }
      }
      localStorage.setItem('cosmic-registration-data', JSON.stringify(reg));
    } catch (_) { /* best-effort; the engine will route to registration if truly missing */ }

    setPendingUpgradeTier(null);
    // skipIntro: our interstitial already set context; don't re-show the 4-page intro.
    navigate(`/cosmic-flow?cosmic=true&tier=${tierLower}&continue=true&skipIntro=true`);
  };

  // Navigation state - cosmic tab system
  const [activeTab, setActiveTab] = useState('overview');
  // 2026-10-30 (Dan): an APC client's results are part of their coaching. They see no
  // coaching pitch and no Careers tab; APC's own portal carries that work. Known from the
  // launch in this tab, or from the saved result belonging to a client.
  const [apcClient, setApcClient] = useState(() => !!launchIdentity());
  const [results, setResults] = useState(propResults);
  const [selectedPreference, setSelectedPreference] = useState(null);
  
  // Sync internal state when prop changes (fixes test page regeneration)
  useEffect(() => {
    if (propResults) {
      console.log('🔄 CosmicResultsView: Syncing with new propResults');
      setResults(propResults);
    }
  }, [propResults]);

  // Keyboard navigation: Left/Right arrows page through the results tabs.
  useEffect(() => {
    const TAB_ORDER = apcClient ? ['overview', 'domains'] : ['overview', 'domains', 'careers'];
    const onKeyDown = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      setActiveTab((current) => {
        const idx = TAB_ORDER.indexOf(current);
        const next = e.key === 'ArrowRight'
          ? Math.min(TAB_ORDER.length - 1, idx + 1)
          : Math.max(0, idx - 1);
        return TAB_ORDER[next];
      });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [apcClient]);
  
  // Preserve all original utility functions
  const getDomainName = (domainId) => {
    const domain = domains.find(d => d.id === domainId);
    return domain ? domain.name : 'Unknown Domain';
  };
  
  const getDomainColor = (domainId) => {
    if (DOMAIN_COLORS[domainId]) {
      return DOMAIN_COLORS[domainId];
    }
    const domain = domains.find(d => d.id === domainId);
    return domain ? domain.color : '#6b7280';
  };
  
  // Demo support (preserved exactly)
  const getQueryParam = (key) => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(key);
    } catch (_) {
      return null;
    }
  };
  
  // All original logic preserved
  useEffect(() => {
    // Load results from props, a specific result id (coach view), stored data, or demo
    if (!propResults) {
      const resultId = getQueryParam('resultId');
      if (resultId) {
        // Wait for Firebase Auth to settle. If the viewer (coach/staff) is not
        // signed in on this origin the Firestore read would be denied, so hold
        // off and let the sign-in gate render instead of firing a doomed request.
        if (authLoading || !isAuthenticated) return;
        loadResultById(resultId);
        return;
      }
      const isDemoMode = getQueryParam('demo') === '1';
      if (isDemoMode) {
        // Demo data loading logic preserved
        loadDemoResults();
      } else {
        loadStoredResults();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propResults, authLoading, isAuthenticated]);

  // Load a specific result document by id (used when a coach opens a client's
  // result from the CRM via /results?resultId=...). Reads from Firestore and
  // applies the stored tier so the full constellation renders for the coach.
  const loadResultById = async (resultId) => {
    setCoachViewError('');
    try {
      const record = await getResultById(resultId);
      if (record && record.clientId) setApcClient(true);
      if (record && record.resultsData) {
        if (record.tier) setOverrideTier(String(record.tier).toLowerCase());
        const processed = ResultsProcessor.transform(record.resultsData) || record.resultsData;
        setResults(processed);
      } else {
        console.warn('No assessment result found for id:', resultId);
        setCoachViewError('No assessment result was found for this link.');
      }
    } catch (error) {
      console.error('Failed to load result by id:', error);
      setCoachViewError(
        error?.code === 'permission-denied'
          ? 'This result is not attributed to your account. Ask an APC admin if you need access.'
          : 'Something went wrong loading this result. Please try again.'
      );
    }
  };

  // Coach/staff sign-in for the result-by-id view. Firebase Auth is per-origin,
  // so even a coach already signed into the CRM must authenticate here. Once the
  // popup completes, onAuthStateChanged flips isAuthenticated and the load effect
  // above re-runs and fetches the result.
  const handleCoachSignIn = async () => {
    setCoachSigningIn(true);
    setCoachViewError('');
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Coach sign-in error:', error);
      setCoachViewError('Sign-in failed. Use your @aportlandcareer.com Google account.');
    } finally {
      setCoachSigningIn(false);
    }
  };

  // The wrong account may already be signed in on this origin (e.g. a personal
  // Google account that isn't APC staff and isn't this result's coach). Sign out
  // and re-prompt the account chooser so the coach can pick their APC account.
  const handleSwitchAccount = async () => {
    setCoachSigningIn(true);
    setCoachViewError('');
    try {
      await signOut();
      await signInWithGoogle();
    } catch (error) {
      console.error('Account switch error:', error);
      setCoachViewError('Sign-in failed. Use your @aportlandcareer.com Google account.');
    } finally {
      setCoachSigningIn(false);
    }
  };
  
  const loadDemoResults = () => {
    // Demo results logic preserved exactly
    console.log('Loading demo results for cosmic view');
    // Implementation preserved from original
  };
  
  const loadStoredResults = async () => {
    // Enhanced logic for both cosmic and legacy assessment results
    // Now includes ResultsProcessor transformation for cosmic compatibility
    try {
      let rawResults = null;
      let dataSource = '';
      
      // First priority: Check for cosmic psychometric engine results (generic key)
      const cosmicResults = localStorage.getItem('cosmicAssessmentResults');
      if (cosmicResults) {
        const cosmicData = JSON.parse(cosmicResults);
        console.log('Loading cosmic psychometric results:', cosmicData);
        rawResults = cosmicData.results;
        dataSource = 'cosmic-localStorage';
      }
      
      // 1b: Fallback - check tier-specific keys if generic key is missing
      if (!rawResults) {
        const urlParams = new URLSearchParams(window.location.search);
        const tierParam = (urlParams.get('tier') || '').toUpperCase();
        const tiersToCheck = tierParam ? [tierParam, 'FREE', 'ENHANCED', 'PREMIUM'] : ['FREE', 'ENHANCED', 'PREMIUM'];
        
        for (const tier of tiersToCheck) {
          const tierResults = localStorage.getItem(`cosmicAssessmentResults_${tier}`);
          if (tierResults) {
            const tierData = JSON.parse(tierResults);
            console.log(`Loading tier-specific results (${tier}):`, tierData);
            rawResults = tierData.results;
            dataSource = `cosmic-tier-${tier}-localStorage`;
            break;
          }
        }
      }
      
      // Second priority: Try to load from Firestore if user is signed in
      if (!rawResults) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            const latest =
              (await getResultsForUser(currentUser.uid)) ||
              (currentUser.email ? await getResultsByEmail(currentUser.email) : null);
            if (latest && latest.resultsData) {
              console.log('Loading results from Firestore:', latest.id);
              rawResults = latest.resultsData;
              dataSource = 'firestore';
            }
          } catch (dbError) {
            console.warn('Failed to load results from Firestore:', dbError);
          }
        }
      }
      
      // Third priority: Check for legacy assessment results
      if (!rawResults) {
        const storedResults = localStorage.getItem('assessmentResults');
        if (storedResults) {
          console.log('Loading legacy assessment results');
          rawResults = JSON.parse(storedResults);
          dataSource = 'legacy-localStorage';
        }
      }
      
      // Transform raw results through ResultsProcessor for cosmic compatibility
      if (rawResults) {
        console.log(`🔄 Processing ${dataSource} results through ResultsProcessor`);
        const processedResults = ResultsProcessor.transform(rawResults);
        
        if (processedResults) {
          console.log('✅ Results successfully processed for cosmic display');
          setResults(processedResults);
          return;
        } else {
          console.warn('⚠️ ResultsProcessor returned null, using raw results');
          setResults(rawResults);
          return;
        }
      }
      
      console.log('No stored assessment results found');
    } catch (error) {
      console.error('Error loading stored results:', error);
    }
  };
  
  // Preserve all original data processing
  const getTopDomains = (results, count = 2) => {
    if (!results?.domains) return [];
    
    return Object.entries(results.domains)
      .sort(([,a], [,b]) => b - a)
      .slice(0, count)
      .map(([domainId]) => domainId);
  };
  
  const getVisiblePreferences = (tier) => {
    if (!results?.topPreferences) return [];
    
    const allPrefs = [...results.topPreferences].sort((a, b) => b.score - a.score);
    
    switch(tier) {
      case USER_TIERS.FREE:
        const topDomains = getTopDomains(results, 1);
        return allPrefs
          .filter(pref => topDomains.includes(pref.domainId))
          .slice(0, 3);
      case USER_TIERS.ENHANCED:
        return allPrefs.slice(0, 8);
      case USER_TIERS.PREMIUM:
        return allPrefs.slice(0, 8);
      default:
        return allPrefs.slice(0, 3);
    }
  };
  
  // Handle share completion (preserved exactly)
  const handleShareComplete = (sharedData) => {
    console.log('Results shared successfully:', sharedData);
    // Original implementation preserved
  };
  
  if (!results) {
    const coachResultId = !propResults ? getQueryParam('resultId') : null;

    // Coach/staff opened a client's result link but is not signed in on this
    // origin (Firebase Auth does not span the CRM and assessment domains). Show a
    // sign-in gate rather than an endless spinner that hides a permissions error.
    if (coachResultId && !authLoading && !isAuthenticated) {
      return (
        <div className="cosmic-results-loading">
          <div className="cosmic-loading-container cosmic-coach-gate">
            <h2 className="cosmic-coach-gate__title">View client results</h2>
            <p className="cosmic-coach-gate__text">
              Sign in with your A Portland Career account to open this client's
              assessment constellation.
            </p>
            {coachViewError && (
              <p className="cosmic-coach-gate__error">{coachViewError}</p>
            )}
            <button
              type="button"
              className="cosmic-coach-gate__btn"
              onClick={handleCoachSignIn}
              disabled={coachSigningIn}
            >
              {coachSigningIn ? 'Signing in...' : 'Sign in with Google'}
            </button>
          </div>
        </div>
      );
    }

    // Signed in, but the read failed (not your client / not found / network).
    if (coachResultId && coachViewError) {
      return (
        <div className="cosmic-results-loading">
          <div className="cosmic-loading-container cosmic-coach-gate">
            <h2 className="cosmic-coach-gate__title">Can't open this result</h2>
            <p className="cosmic-coach-gate__error">{coachViewError}</p>
            <p className="cosmic-coach-gate__text">
              Make sure you're signed in with your A Portland Career account.
            </p>
            <button
              type="button"
              className="cosmic-coach-gate__btn"
              onClick={handleSwitchAccount}
              disabled={coachSigningIn}
            >
              {coachSigningIn ? 'Signing in...' : 'Sign in with a different account'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="cosmic-results-loading">
        <div className="cosmic-loading-container">
          <div className="cosmic-loading-spinner"></div>
          <p>Loading your cosmic constellation...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="cosmic-results-view">
      {/* Cosmic Background Effects */}
      <div className="cosmic-background">
        <div className="cosmic-stars-field"></div>
        <div className="cosmic-nebula-effect"></div>
      </div>
      
      {/* Main Content Container */}
      <div className="cosmic-results-content">
        
        {/* Fixed Cosmic Navigation */}
        <CosmicResultsNavigation 
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hideCareers={apcClient}
          shareControl={!shared ? (
            <CosmicShareButton results={results} tier={testingTier} />
          ) : null}
        />
        
        {/* Tab Content Area */}
        <div className="cosmic-tab-content">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="cosmic-overview-tab">
              {/* Full-width Galaxy Header - Appears first */}
              <div className="cosmic-galaxy-header-standalone">
                <div className="cosmic-card cosmic-card--professional">
                  <div className="cosmic-card__header cosmic-card__header--with-cta">
                    <div className="cosmic-header-left">
                      <h3 className="cosmic-card__title"><CosmicIcon name="results_domains" size={22} decorative /> Your Career Galaxy</h3>
                      
                      {/* Tier-specific instructional messaging */}
                      {testingTier === USER_TIERS.FREE && (
                        <div className="cosmic-galaxy-instructions">
                          <p className="cosmic-card__subtitle cosmic-card__subtitle--instruction">
                            Your <strong className="cosmic-constellation-preview-name">"{getConstellationName(results)}"</strong> constellation below is a <strong className="cosmic-preview-badge">preview</strong> — the top 3 element scores are estimates based on your domain response patterns.
                          </p>
                          <p className="cosmic-card__subtitle cosmic-card__subtitle--hint">
                            <CosmicIcon name="ui_chart" size={15} decorative /> Click any domain bar to explore elements. Your constellation may shift as you confirm more elements with additional assessment items.
                          </p>
                        </div>
                      )}
                      
                      {testingTier === USER_TIERS.ENHANCED && (
                        <div className="cosmic-galaxy-instructions">
                          <p className="cosmic-card__subtitle cosmic-card__subtitle--instruction">
                            Your <strong>top 8 elements</strong> are confirmed with high precision — these form your career constellation.
                          </p>
                          <p className="cosmic-card__subtitle cosmic-card__subtitle--hint">
                            <CosmicIcon name="ui_chart" size={15} decorative /> Click any domain bar to explore how elements within that area connect to career recommendations and role fit.
                          </p>
                        </div>
                      )}
                      
                      {testingTier === USER_TIERS.PREMIUM && (
                        <div className="cosmic-galaxy-instructions">
                          <p className="cosmic-card__subtitle cosmic-card__subtitle--instruction">
                            All <strong>45 elements</strong> are mapped across five domains — your complete motivational profile.
                          </p>
                          <p className="cosmic-card__subtitle cosmic-card__subtitle--hint">
                            <CosmicIcon name="ui_chart" size={15} decorative /> Click any domain bar to explore element scores and see how they drive career fit recommendations.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Upgrade CTA - only show for non-Premium tiers (never in public shared view) */}
                    {testingTier !== USER_TIERS.PREMIUM && !shared && (
                      <div className="cosmic-header-cta">
                        <button 
                          className="cosmic-cta cosmic-cta--sm"
                          onClick={() => {
                            const upgradeSection = document.querySelector('.cosmic-upgrade-section');
                            if (upgradeSection) {
                              upgradeSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }}
                        >
                          <span className="cosmic-cta-icon"><CosmicIcon name="ui_bolt" size={18} decorative /></span>
                          {testingTier === USER_TIERS.FREE ? 'Confirm Your Constellation' : 'Unlock All 45 Elements'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Full-width Star Grid - 8 stars in a row */}
              <CosmicStarGridStandalone 
                results={results}
                tier={testingTier}
                domainColors={DOMAIN_COLORS}
              />
              
              {/* Constellation + Domain Galaxy Side-by-Side Layout */}
              <div className="cosmic-overview-duo">
                {/* Constellation Display - Left on desktop, First on mobile */}
                <div className="cosmic-overview-duo__constellation">
                  <CosmicConstellationTab 
                    results={results}
                    tier={testingTier}
                    domainColors={DOMAIN_COLORS}
                    hideStarGrid={true}
                  />
                </div>
                
                {/* Domains Galaxy - Right on desktop, Second on mobile */}
                <div className="cosmic-overview-duo__domains">
                  <CosmicDomainsGalaxy 
                    results={results}
                    tier={testingTier}
                    domainColors={DOMAIN_COLORS}
                    domains={domains}
                    preferences={preferences}
                    hideHeader={true}
                  />
                </div>
              </div>
              
              {!shared && !apcClient && (
                <CosmicShareWithCoach 
                  results={results}
                  onShareComplete={handleShareComplete}
                />
              )}
              
              {/* Upgrade prompt for non-premium users (hidden in public shared view) */}
              {testingTier !== USER_TIERS.PREMIUM && !shared && (
                <div className="cosmic-upgrade-section">
                  <UpgradePrompt
                    targetTier={testingTier === USER_TIERS.FREE ? USER_TIERS.ENHANCED : USER_TIERS.PREMIUM}
                    context="results_overview"
                    overrideTier={testingTier}
                    onSuccess={handleUpgradeSuccess}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Domains Galaxy Tab - Content moved to Overview */}
          {activeTab === 'domains' && (
            <CosmicDomainStrengths
              results={results}
              tier={testingTier}
              domainColors={DOMAIN_COLORS}
            />
          )}
          
          {/* Career Directions Tab */}
          {activeTab === 'careers' && !apcClient && (
            <CosmicCareerDirections 
              results={results}
              tier={testingTier}
              domainColors={DOMAIN_COLORS}
            />
          )}
          
        </div>
        
      </div>
      
      {/* Post-payment continuation interstitial. Shown after a successful upgrade
          payment; Continue re-enters the cosmic flow at the new tier so the engine
          serves the next stage's items (or instant-unlocks Premium). */}
      {!shared && pendingUpgradeTier && (
        <div
          className="cosmic-upgrade-interstitial-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', background: 'rgba(5, 5, 20, 0.82)', backdropFilter: 'blur(6px)'
          }}
        >
          <div className="cosmic-card cosmic-card--elevated" style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
            <div className="cosmic-card__header">
              <h3 className="cosmic-card__title" style={{ justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CosmicIcon name="brand_spark" size={24} decorative />
                {pendingUpgradeTier === USER_TIERS.PREMIUM ? 'Complete Universe Unlocked' : 'Enhanced Unlocked'}
              </h3>
            </div>
            <div className="cosmic-card__body">
              <p className="cosmic-card__subtitle">
                {pendingUpgradeTier === USER_TIERS.PREMIUM
                  ? "You've unlocked all 45 elements. Let's reveal your complete constellation."
                  : "You've unlocked your Stellar Map. Next, a few quick questions confirm your top 8 elements with high precision."}
              </p>
            </div>
            <div className="cosmic-card__footer" style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
              <button className="cosmic-cta cosmic-cta--lg" onClick={continueUpgrade}>
                {pendingUpgradeTier === USER_TIERS.PREMIUM ? 'Reveal my full constellation' : 'Continue to your questions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal (preserved exactly; never in public shared view) */}
      {!shared && paymentModal}
    </div>
  );
};

export default CosmicResultsView;
